import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import type { Aniversariantes_EnviarInput } from '../contracts/Aniversariantes_EnviarInput';
import '../contracts/Aniversariantes_EnviarInput';
import '../contracts/Aniversariantes_ListaInput';
import '../contracts/Aniversariantes_MensagemConfigInput';
import '../contracts/Aniversariantes_SemanaResponse';
import '../contracts/Aniversariantes_MesResponse';
import '../contracts/Aniversariantes_ListaResponse';
import '../contracts/Aniversariantes_HistoricoResponse';
import '../contracts/Aniversariantes_EnviarResponse';
import '../contracts/Aniversariantes_MensagemconfigGetResponse';
import '../contracts/Aniversariantes_MensagemconfigPostResponse';

interface ApiResponse<T> {
  success: boolean;
  dados?: T;
  data?: T;
  error?: string;
}

interface AniversarianteRaw {
  cliente_data_nascimento?: string | null;
  cliente_nome?: string;
  idade_anos?: number | null;
  enviado?: boolean | number | null;
  [key: string]: unknown;
}

interface Aniversariante extends AniversarianteRaw {
  idade_anos: number | null;
  enviado: boolean;
}

interface MensagemConfigDados {
  mensagem_aniversariante?: string | null;
}

interface FranquiaInfo {
  nome?: string;
  desconto_aniversario?: string;
}

/**
 * Composable for Aniversariantes (Birthday) operations
 * Provides methods to interact with the Aniversariantes API
 */
export function useAniversariantes() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  /**
   * Buscar aniversariantes da semana
   */
  const fetchAniversariantesDaSemana = async (dataInicio: string | null = null): Promise<Aniversariante[]> => {
    log('useAniversariantes', 'fetchAniversariantesDaSemana', 'Fetching aniversariantes da semana', dataInicio);
    isLoading.value = true;
    error.value = null;

    try {
      // Se não forneceu data início, usar hoje
      if (!dataInicio) {
        dataInicio = new Date().toISOString().split('T')[0];
      }

      const response = await httpService.get<ApiResponse<AniversarianteRaw[]>>('/index.php/aniversariantes/semana', {
        responseContract: 'Aniversariantes_SemanaResponse',
      });
      log('useAniversariantes', 'fetchAniversariantesDaSemana', 'Response', response);

      if (response.success) {
        const dados = response.dados || [];
        return processarAniversariantes(dados);
      } else {
        throw new Error(response.error || 'Erro ao buscar aniversariantes');
      }
    } catch (err) {
      log('useAniversariantes', 'fetchAniversariantesDaSemana', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Buscar aniversariantes do mês
   */
  const fetchAniversariantesDoMes = async (mes: number, ano: number): Promise<Aniversariante[]> => {
    log('useAniversariantes', 'fetchAniversariantesDoMes', 'Fetching aniversariantes do mês', { mes, ano });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<ApiResponse<AniversarianteRaw[]>>(`/index.php/aniversariantes/mes/${mes}/${ano}`, {
        responseContract: 'Aniversariantes_MesResponse',
      });
      log('useAniversariantes', 'fetchAniversariantesDoMes', 'Response', response);

      if (response.success) {
        const dados = response.dados || [];
        return processarAniversariantes(dados);
      } else {
        throw new Error(response.error || 'Erro ao buscar aniversariantes');
      }
    } catch (err) {
      log('useAniversariantes', 'fetchAniversariantesDoMes', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Buscar aniversariantes do período (driven by useFiltro DataInicio/DataFim).
   * Replaces fetchAniversariantesDaSemana / fetchAniversariantesDoMes for any
   * caller that respects the global filter (the convention going forward).
   */
  const fetchAniversariantes = async (dataInicio?: string, dataFim?: string): Promise<Aniversariante[]> => {
    log('useAniversariantes', 'fetchAniversariantes', 'Fetching for period', { dataInicio, dataFim });
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Record<string, string> = {};
      if (dataInicio) payload.data_inicio = dataInicio;
      if (dataFim) payload.data_fim = dataFim;

      validate('Aniversariantes_ListaInput', payload);

      const response = await httpService.post<ApiResponse<AniversarianteRaw[]>>(
        '/index.php/aniversariantes/lista',
        payload,
        {
          inputContract: 'Aniversariantes_ListaInput',
          responseContract: 'Aniversariantes_ListaResponse',
        },
      );
      log('useAniversariantes', 'fetchAniversariantes', 'Response', response);

      if (response.success) {
        const dados = response.dados || [];
        return processarAniversariantes(dados);
      } else {
        throw new Error(response.error || 'Erro ao buscar aniversariantes');
      }
    } catch (err) {
      log('useAniversariantes', 'fetchAniversariantes', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Buscar histórico de envios para um cliente
   */
  const fetchHistorico = async (clienteId: number): Promise<unknown[]> => {
    log('useAniversariantes', 'fetchHistorico', 'Fetching historico', clienteId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<ApiResponse<unknown[]>>(`/index.php/aniversariantes/historico/${clienteId}`, {
        responseContract: 'Aniversariantes_HistoricoResponse',
      });
      log('useAniversariantes', 'fetchHistorico', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao buscar histórico');
      }
    } catch (err) {
      log('useAniversariantes', 'fetchHistorico', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Marcar mensagem como enviada
   */
  const marcarEnviado = async (
    clienteId: number,
    franquiaId: number,
    mensagem: string,
    status: string = 'enviado',
    whatsappMensagemId: number | null = null
  ): Promise<unknown> => {
    log('useAniversariantes', 'marcarEnviado', 'Chamado', { clienteId, franquiaId, mensagem, status, whatsappMensagemId });
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Aniversariantes_EnviarInput = {
        cliente_id: clienteId,
        franquia_id: franquiaId,
        mensagem: mensagem
      };

      if (status !== 'enviado') {
        payload.status = status;
      }

      if (whatsappMensagemId) {
        payload.whatsapp_mensagem_id = whatsappMensagemId;
      }

      validate('Aniversariantes_EnviarInput', payload as unknown as Record<string, unknown>);
      log('useAniversariantes', 'marcarEnviado', 'Payload', payload);
      log('useAniversariantes', 'marcarEnviado', 'Posting', '/index.php/aniversariantes/enviar');

      const response = await httpService.post<ApiResponse<unknown>>('/index.php/aniversariantes/enviar', payload, {
        responseContract: 'Aniversariantes_EnviarResponse',
      });

      log('useAniversariantes', 'marcarEnviado', 'Response', response);

      if (response.success) {
        log('useAniversariantes', 'marcarEnviado', 'Success', response.dados);
        return response.dados;
      } else {
        log('useAniversariantes', 'marcarEnviado', 'Error', response.error);
        throw new Error(response.error || 'Erro ao registrar envio');
      }
    } catch (err) {
      log('useAniversariantes', 'marcarEnviado', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Buscar mensagem configurada para uma franquia
   */
  const fetchMensagemConfig = async (franquiaId: number): Promise<string | null> => {
    log('useAniversariantes', 'fetchMensagemConfig', 'Fetching mensagem config', franquiaId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<ApiResponse<MensagemConfigDados>>(`/index.php/aniversariantes/mensagem-config/${franquiaId}`, {
        responseContract: 'Aniversariantes_MensagemconfigGetResponse',
      });
      log('useAniversariantes', 'fetchMensagemConfig', 'Response', response);

      if (response.success) {
        return response.dados?.mensagem_aniversariante || null;
      } else {
        throw new Error(response.error || 'Erro ao buscar mensagem');
      }
    } catch (err) {
      log('useAniversariantes', 'fetchMensagemConfig', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Salvar mensagem configurada para uma franquia
   */
  const saveMensagemConfig = async (franquiaId: number, mensagem: string): Promise<boolean> => {
    log('useAniversariantes', 'saveMensagemConfig', 'Saving mensagem config', { franquiaId, mensagem });
    isLoading.value = true;
    error.value = null;

    try {
      const payload = { mensagem };
      validate('Aniversariantes_MensagemConfigInput', payload);
      const response = await httpService.post<ApiResponse<unknown>>(`/index.php/aniversariantes/mensagem-config/${franquiaId}`, payload, {
        responseContract: 'Aniversariantes_MensagemconfigPostResponse',
      });
      log('useAniversariantes', 'saveMensagemConfig', 'Response', response);

      if (response.success) {
        return true;
      } else {
        throw new Error(response.error || 'Erro ao salvar mensagem');
      }
    } catch (err) {
      log('useAniversariantes', 'saveMensagemConfig', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // =========================================================================
  // Helpers
  // =========================================================================

  /**
   * Calcular idade a partir da data de nascimento
   */
  const calcularIdade = (dataNascimento: string | Date | null | undefined): number | null => {
    if (!dataNascimento) return null;

    const nascimento = new Date(dataNascimento);
    const hoje = new Date();
    const idade = hoje.getFullYear() - nascimento.getFullYear();
    const mesDiff = hoje.getMonth() - nascimento.getMonth();

    if (mesDiff < 0 || (mesDiff === 0 && hoje.getDate() < nascimento.getDate())) {
      return idade - 1;
    }

    return idade;
  };

  /**
   * Substituir placeholders na mensagem
   * Placeholders suportados: {nome}, {primeiro_nome}, {idade}, {dia}, {mes}, {franquia}, {desconto}
   */
  const substituirPlaceholders = (
    mensagem: string,
    cliente: AniversarianteRaw,
    franquia: FranquiaInfo | null | undefined
  ): string => {
    if (!mensagem) return '';

    const primeiroNome = cliente.cliente_nome ? cliente.cliente_nome.split(' ')[0] : '';
    const idade = calcularIdade(cliente.cliente_data_nascimento);

    let dataAniversario: Date | null = null;
    if (cliente.cliente_data_nascimento) {
      dataAniversario = new Date(cliente.cliente_data_nascimento);
    }

    const dia = dataAniversario ? dataAniversario.getDate() : '';
    const mes = dataAniversario ? dataAniversario.toLocaleDateString('pt-BR', { month: 'long' }) : '';

    const replacements: Record<string, string | number> = {
      '{nome}': cliente.cliente_nome || '',
      '{primeiro_nome}': primeiroNome,
      '{idade}': idade !== null ? idade : '',
      '{dia}': dia,
      '{mes}': mes,
      '{franquia}': franquia?.nome || '',
      '{desconto}': franquia?.desconto_aniversario || '10%'
    };

    let resultado = mensagem;
    for (const [key, value] of Object.entries(replacements)) {
      resultado = resultado.replace(new RegExp(key, 'g'), String(value));
    }

    return resultado;
  };

  /**
   * Processar lista de aniversariantes (adicionar campos calculados)
   */
  const processarAniversariantes = (aniversariantes: AniversarianteRaw[] | unknown): Aniversariante[] => {
    if (!Array.isArray(aniversariantes)) return [];

    return aniversariantes.map((item: AniversarianteRaw) => ({
      ...item,
      idade_anos: item.idade_anos ?? calcularIdade(item.cliente_data_nascimento),
      enviado: !!item.enviado
    }));
  };

  return {
    isLoading,
    error,
    fetchAniversariantes,
    fetchAniversariantesDaSemana,
    fetchAniversariantesDoMes,
    fetchHistorico,
    marcarEnviado,
    fetchMensagemConfig,
    saveMensagemConfig,
    calcularIdade,
    substituirPlaceholders
  };
}
